import React from 'react'
import { useTranslation } from 'react-i18next'
import {observer} from 'mobx-react'
import classNames from 'classnames'
import {Card as AntCard, Descriptions, Typography, Button} from 'antd'
import {RightOutlined, DeleteOutlined} from '@ant-design/icons'

import {useScreenType} from '@/hooks'
import {Card, GeolocationMap, Icon, OutletStatus} from '@/components'
import {OUTLET_CARD} from '@/constants/playwright-ids'

import {OutletCardProps} from './props'
import styles from './OutletCard.scss'

export const OutletCard: React.FC<OutletCardProps> = observer(
    ({
        onClick,
        mid,
        settlementAccount,
        directDebitAccount,
        address,
        isActive,
        storeName,
        latitude,
        longitude,
        isEditable = false,
        showStatus = true,
        onEdit,
        showIconButton,
        isDeletable = false,
        onDelete
    }) => {
        const { t } = useTranslation()
        const {isMobile} = useScreenType()

        const handleCardClick = (e: React.MouseEvent) => {
            if (isEditable && onEdit && (e.target as HTMLElement).closest('button')) {
                return
            }
            onClick()
        }

        const handleEditClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            if (onEdit) {
                onEdit()
            }
        }

        const handleDeleteClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            if (onDelete) {
                onDelete()
            }
        }

        return (
            <Card
                hoverable
                className={classNames(styles.card, {
                    [styles.cardMobile]: isMobile,
                    [styles.cardEditable]: isEditable
                })}
                onClick={handleCardClick}
                data-test-id={OUTLET_CARD}
            >
                {isMobile && !isEditable && <RightOutlined className={styles.rightIcon} />}

                <div className={styles.cardContent}>
                    <AntCard.Meta
                        title={storeName}
                        description={
                            <>
                                <Descriptions
                                    className={styles.descriptionLabel}
                                    title=''
                                    layout={'vertical'}
                                    column={1}
                                >
                                    {address && (
                                        <Descriptions.Item label={t('OutletCard.labels.address')}>
                                            {address}
                                        </Descriptions.Item>
                                    )}
                                    {mid && (
                                        <Descriptions.Item label={t('OutletCard.labels.mid')}>
                                            <Typography.Paragraph copyable>
                                                {mid}
                                            </Typography.Paragraph>
                                        </Descriptions.Item>
                                    )}
                                    {settlementAccount && (
                                        <Descriptions.Item
                                            label={t('OutletCard.labels.settlementAccount')}
                                        >
                                            {settlementAccount}
                                        </Descriptions.Item>
                                    )}
                                    {directDebitAccount && (
                                        <Descriptions.Item
                                            label={t('OutletCard.labels.directDebitAccount')}
                                        >
                                            {directDebitAccount}
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                                {!isMobile && <br />}
                                {showStatus && <OutletStatus isActive={isActive} />}
                            </>
                        }
                    />

                    {isEditable && onEdit && (
                        <div className={styles.editActions}>
                            {showIconButton ? (
                                <>
                                    <Button
                                        type='link'
                                        icon={<Icon type='pencil' />}
                                        onClick={handleEditClick}
                                        className={styles.editButton}
                                    />
                                    {isDeletable && (
                                        <Button
                                            type='link'
                                            icon={<Icon type='trash' />}
                                            onClick={handleDeleteClick}
                                            className={styles.deleteButton}
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    <Button
                                        type='default'
                                        className={styles.editButton}
                                        onClick={handleEditClick}
                                    >
                                        {'Edit details'}
                                    </Button>
                                    {isDeletable && (
                                        <Button
                                            type='default'
                                            icon={<DeleteOutlined />}
                                            onClick={handleDeleteClick}
                                            className={styles.deleteButton}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {isMobile && <br />}
                {latitude && longitude && (
                    <GeolocationMap
                        center={{
                            lat: latitude,
                            lng: longitude
                        }}
                        className={classNames(styles.map, {[styles.mapMobile]: isMobile})}
                    />
                )}
            </Card>
        )
    }
)
